import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';
import BatchStatusBadge from '../../active-batch-management/components/BatchStatusBadge';

function calcAge(createdAt) {
  if (!createdAt) return 0;
  return Math.floor((Date.now() - new Date(createdAt)?.getTime()) / (1000 * 60 * 60 * 24));
}

export default function InventoryAgePanel({ batches, riskThresholds }) {
  const alertDays = riskThresholds?.inventory_age_alert_days || 30;

  const alertedLots = useMemo(() => {
    return (batches || [])?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status))?.map(b => ({ ...b, age: calcAge(b?.created_at) }))?.filter(b => b?.age >= alertDays)?.sort((a, b) => b?.age - a?.age);
  }, [batches, alertDays]);

  const getSeverity = (age) => {
    if (age >= alertDays * 2) return { label: 'Crítico', bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' };
    if (age >= alertDays * 1.5) return { label: 'Alto', bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' };
    return { label: 'Alerta', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' };
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Clock" size={18} color="var(--color-warning)" strokeWidth={2} />
          <h2 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            <Tooltip
              title="Antigüedad de Inventario"
              description="Lista de lotes activos que superan el umbral de días configurado. Los lotes más antiguos tienen mayor riesgo de depreciación o dificultad de venta."
            >
              Antigüedad de Inventario
            </Tooltip>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip
            title="Umbral de alerta"
            description={`Los lotes con más de ${alertDays} días desde su creación aparecen en esta lista. Configurable en Ajustes → Umbrales de Riesgo.`}
          >
            <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Umbral: {alertDays} días</span>
          </Tooltip>
          {alertedLots?.length > 0 && (
            <Tooltip
              title="Lotes sobre umbral"
              description={`${alertedLots?.length} lote${alertedLots?.length > 1 ? 's superan' : ' supera'} el umbral de ${alertDays} días. Revisa su estado y considera acelerar la venta.`}
            >
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-xs font-semibold text-amber-700">
                {alertedLots?.length} lote{alertedLots?.length > 1 ? 's' : ''}
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="p-4">
        {alertedLots?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Icon name="CheckCircle" size={32} color="var(--color-success)" className="mb-2 opacity-60" />
            <p className="text-sm" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Todos los lotes dentro del umbral de {alertDays} días</p>
          </div>
        ) : (
          <div className="space-y-2">
            {alertedLots?.map(batch => {
              const sev = getSeverity(batch?.age);
              const exposure = (parseFloat(batch?.purchase_price) || 0) * (parseFloat(batch?.weight_t) || 0);
              return (
                <div key={batch?.id} className={`flex items-center justify-between p-3 rounded-lg border ${sev?.bg} transition-colors`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sev?.dot}`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${sev?.text}`} style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                        {batch?.category_name || batch?.metal_name || '—'}
                      </p>
                      <p className="text-xs text-muted-foreground">{batch?.batch_code || batch?.id?.slice(0, 8)} · {(parseFloat(batch?.weight_t) || 0)?.toFixed(2)} t</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-muted-foreground">Exposición</p>
                      <Tooltip
                        title="Exposición del lote"
                        description="Capital comprometido en este lote: Precio de Compra × Peso (t)."
                      >
                        <p className={`text-sm font-mono font-semibold ${sev?.text}`}>{exposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €</p>
                      </Tooltip>
                    </div>
                    <Tooltip
                      title={`Severidad: ${sev?.label}`}
                      description={
                        sev?.label === 'Crítico'
                          ? `Este lote lleva más de ${alertDays * 2} días en inventario. Riesgo muy alto de depreciación.`
                          : sev?.label === 'Alto'
                          ? `Este lote lleva más de ${Math.round(alertDays * 1.5)} días en inventario. Considera priorizar su venta.`
                          : `Este lote supera el umbral de ${alertDays} días. Monitoriza su evolución.`
                      }
                    >
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${sev?.badge}`}>
                        <Icon name="Clock" size={10} color="currentColor" />
                        {batch?.age} días
                      </span>
                    </Tooltip>
                    <BatchStatusBadge status={batch?.batch_status} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
